/**
 * Keyboard input, shaped for boat handling.
 *
 * Throttle and rudder are *rates*, not positions: holding W walks the throttle
 * up and it stays where you set it. The rudder drifts gently back to midships
 * when untouched — young players let go and expect to go straight. `R` snaps
 * it to midships immediately, `X` pulls the throttle to stop.
 */

export class Input {
  /** -1..1, persists until changed. */
  throttle = 0
  /** -1..1, persists until changed. Midships on demand via R. */
  rudder = 0

  private readonly held = new Set<string>()
  private readonly virtualHeld = new Set<string>()
  private readonly pressed = new Set<string>()
  /** True while the throttle is parked at the zero crossing under a held key. */
  private throttleDetent = false

  constructor(target: Window = window) {
    target.addEventListener('keydown', (event) => {
      if (event.target instanceof HTMLElement) {
        if (event.target.closest('.title-screen, input, textarea, [contenteditable]')) return
        // Let focused buttons keep their native keyboard activation. WASD still drives.
        if (event.target.closest('button') && ['Space', 'Enter'].includes(event.code)) return
      }
      // Keep space and arrows from scrolling the page.
      if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code)) event.preventDefault()
      if (event.repeat) return
      this.held.add(event.code)
      this.pressed.add(event.code)
    })
    target.addEventListener('keyup', (event) => {
      this.held.delete(event.code)
    })
    target.addEventListener('blur', () => this.releaseAll())
    target.document.addEventListener('visibilitychange', () => {
      if (target.document.hidden) this.releaseAll()
    })
  }

  isHeld(code: string): boolean {
    return this.held.has(code) || this.virtualHeld.has(code)
  }

  /** On-screen buttons feed the same paths as their keys. */
  virtualPress(code: string): void {
    this.virtualHeld.add(code)
    this.pressed.add(code)
  }

  virtualRelease(code: string): void {
    this.virtualHeld.delete(code)
  }

  /** Release controls without changing the throttle setting. */
  private releaseAll(): void {
    this.held.clear()
    this.virtualHeld.clear()
    this.pressed.clear()
    this.throttleDetent = false
  }

  /** Drop everything — used when handing control back to the menu. */
  reset(): void {
    this.releaseAll()
    this.throttle = 0
    this.rudder = 0
  }

  /** True once per physical key press. Consumed by the call. */
  wasPressed(code: string): boolean {
    return this.pressed.delete(code)
  }

  update(dt: number): void {
    const throttleRate = 0.7
    // Helm eases over rather than slamming hard over — gentle, but quick
    // enough to answer promptly when you ask for a turn.
    const rudderRate = 0.85
    // Gentle drift back to midships when the helm is untouched. Slow enough
    // that a set course holds for a while, fast enough that a child who lets
    // go ends up going straight again without having to find R.
    const recentreRate = 0.35
    // A tap — click, touch, or key press shorter than a frame — lands a fixed
    // step, so the on-screen buttons respond to clicking as well as holding.
    const throttleTap = 0.12
    const rudderTap = 0.1
    const tap = (code: string): boolean => this.pressed.delete(code)

    const before = this.throttle
    if (tap('KeyW') || tap('ArrowUp')) this.throttle += throttleTap
    if (tap('KeyS') || tap('ArrowDown')) this.throttle -= throttleTap
    if (this.isHeld('KeyW') || this.isHeld('ArrowUp')) this.throttle += throttleRate * dt
    if (this.isHeld('KeyS') || this.isHeld('ArrowDown')) this.throttle -= throttleRate * dt
    if (this.isHeld('KeyX')) this.throttle = 0

    // Zero detent: ramping through neutral parks the lever at stop. Holding
    // "down" from full ahead brings you to a halt — it takes a fresh press to
    // go astern, so nobody backs into a quay by holding a button too long.
    if (before !== 0 && Math.sign(this.throttle) !== Math.sign(before)) {
      this.throttle = 0
      this.throttleDetent = true
    }
    if (this.throttleDetent) {
      const throttleHeld =
        this.isHeld('KeyW') || this.isHeld('ArrowUp') || this.isHeld('KeyS') || this.isHeld('ArrowDown')
      if (throttleHeld) this.throttle = 0
      else this.throttleDetent = false
    }

    const port = this.isHeld('KeyA') || this.isHeld('ArrowLeft')
    const starboard = this.isHeld('KeyD') || this.isHeld('ArrowRight')
    if (tap('KeyA') || tap('ArrowLeft')) this.rudder -= rudderTap
    if (tap('KeyD') || tap('ArrowRight')) this.rudder += rudderTap
    if (port) this.rudder -= rudderRate * dt
    if (starboard) this.rudder += rudderRate * dt
    if (!port && !starboard && this.rudder !== 0) {
      const restore = recentreRate * dt
      if (Math.abs(this.rudder) <= restore) this.rudder = 0
      else this.rudder -= Math.sign(this.rudder) * restore
    }
    if (this.isHeld('KeyR')) this.rudder = 0

    this.throttle = Math.min(Math.max(this.throttle, -1), 1)
    this.rudder = Math.min(Math.max(this.rudder, -1), 1)
  }
}
